import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit'

import { initialState } from './configs'
import { getInitFormState } from './helpers/getInitFormState'
import {
  IFormState,
  IInitFormDataFieldPayload,
  IInitFormDataPayload,
  IUpdateModifiedDataPayload,
} from './models'

export const formSlice: Slice<IFormState> = createSlice({
  name: 'form',
  initialState,
  reducers: {
    initFormData: <T>(state: IFormState<T>, action: PayloadAction<IInitFormDataPayload<T>>) => {
      const { formKey, formData } = action.payload

      state[formKey] = getInitFormState(formData)
    },
    initFormField: <T>(
      state: IFormState<{ [key: string]: T }>,
      action: PayloadAction<IInitFormDataFieldPayload<T>>
    ) => {
      const { formKey, defaultValue, field } = action.payload
      const formData = { [field]: defaultValue }

      if (state[formKey]) {
        state[formKey].formData[field] = defaultValue
        state[formKey].modifiedFields = state[formKey].modifiedFields.filter(
          (modifiedField) => modifiedField !== field
        )
      } else {
        state[formKey] = getInitFormState(formData)
      }
    },
    updateFormData: <T>(
      state: IFormState<T>,
      action: PayloadAction<{ formKey: string; formData: T }>
    ) => {
      const { formKey, formData } = action.payload

      if (state[formKey]) {
        state[formKey].formData = formData
        state[formKey].modifiedFields = Object.keys(formData || {})
      } else {
        state[formKey] = getInitFormState(formData)
      }
    },
    updateFormDataField: (state, action: PayloadAction<IUpdateModifiedDataPayload>) => {
      const { formKey, value, field, ignoreModified } = action.payload
      if (state[formKey]) {
        state[formKey].formData[field] = value
        if (!ignoreModified && !state[formKey].modifiedFields.includes(field)) {
          state[formKey].modifiedFields.push(field)
        }
      }
    },
    updateInvalidData: (
      state,
      action: PayloadAction<{ key: string; field: string; validationMessage: string }>
    ) => {
      const { key, field, validationMessage } = action.payload
      if (!validationMessage) {
        if (state[key]?.invalidFields[field]) {
          delete state[key].invalidFields[field]
          state[key].isValid = Object.keys(state[key]?.invalidFields).length === 0
        }
      } else {
        state[key].invalidFields[field] = validationMessage
        state[key].isValid = false
      }
    },
    resetModifiedDataByField: <V>(
      state: IFormState<{ [key: string | number | symbol]: V }>,
      action: PayloadAction<{ formKey: string; field: string | number | symbol; defaultValue?: V }>
    ) => {
      const { formKey, field, defaultValue } = action.payload
      if (state[formKey]) {
        if (defaultValue !== undefined) {
          state[formKey].formData[field] = defaultValue
        }
        state[formKey].modifiedFields = state[formKey].modifiedFields.filter(
          (modifiedField) => modifiedField !== field
        )

        if (state[formKey].invalidFields[field]) {
          delete state[formKey].invalidFields[field]
        }

        state[formKey].isValid = Object.keys(state[formKey].invalidFields).length === 0
      }
    },
    resetAllModifiedDataByKey: <V>(
      state: IFormState<V>,
      action: PayloadAction<string | { formKey: string; defaultValue: V }>
    ) => {
      if (typeof action.payload === 'string') {
        delete state[action.payload]
      } else {
        const { formKey, defaultValue } = action.payload
        state[formKey] = getInitFormState(defaultValue)
      }
    },
    resetValidationData: (state, action: PayloadAction<string>) => {
      const formKey = action.payload
      if (state[formKey]) {
        state[formKey].invalidFields = {}
        state[formKey].isValid = true
      }
    },
  },
})

export const {
  initFormData,
  initFormField,
  updateFormData,
  updateFormDataField,
  updateInvalidData,
  resetAllModifiedDataByKey,
  resetModifiedDataByField,
  resetValidationData,
} = formSlice.actions

export const formReducer = formSlice.reducer
